/**
 * Remembers which menu groups a person collapsed or opened, in cookies the
 * server can read, so a reload renders each group as they left it from the
 * first paint instead of snapping it open or shut after hydration.
 *
 * Two cookies rather than one, because groups start in different states: a
 * group that starts open only needs remembering once it is closed, and one
 * that starts closed only once it is opened. Keeping the closed list in its
 * original cookie means a choice made before groups could start closed still
 * holds.
 *
 * Aliases: collapsed sidebar groups, closed menu sections, sidebar group cookie
 */

import { createIsomorphicFn, createServerOnlyFn } from "@tanstack/react-start";

import { getCookieValue } from "~/lib/utils";

// The sidebar imports this module, so a static server import would ship to the
// browser. createServerOnlyFn severs that edge in the client build.
const importServerCookies = createServerOnlyFn(
  () => import("@tanstack/react-start/server"),
);

export const SIDEBAR_GROUPS_COOKIE = "sidebar_closed_groups";
export const SIDEBAR_OPEN_GROUPS_COOKIE = "sidebar_open_groups";

/** The groups a person closed and the groups they opened, by id. */
export type SidebarGroupChoices = {
  closed: readonly string[];
  opened: readonly string[];
};

/** A group id: lowercase letters, digits and hyphens, e.g. `my-clients`. */
const GROUP_ID = /^[a-z0-9-]+$/;

/** Outside the id alphabet and valid in a cookie value without encoding. */
const SEPARATOR = ".";

const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

/** The group ids in a cookie value; anything unexpected is dropped. */
export function parseSidebarGroups(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(SEPARATOR).filter((id) => GROUP_ID.test(id));
}

export function parseSidebarGroupChoices(
  cookieHeader: string,
): SidebarGroupChoices {
  return {
    closed: parseSidebarGroups(
      getCookieValue(cookieHeader, SIDEBAR_GROUPS_COOKIE),
    ),
    opened: parseSidebarGroups(
      getCookieValue(cookieHeader, SIDEBAR_OPEN_GROUPS_COOKIE),
    ),
  };
}

export function serializeSidebarGroups(ids: Iterable<string>): string {
  return [...ids].filter((id) => GROUP_ID.test(id)).join(SEPARATOR);
}

function writeCookie(name: string, ids: Iterable<string>): void {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${name}=${serializeSidebarGroups(ids)}; path=/; max-age=${ONE_YEAR_IN_SECONDS}; samesite=lax${secure}`;
}

/** Stores both lists for a year, for every page of the site. */
export function writeSidebarGroupChoices(choices: SidebarGroupChoices): void {
  writeCookie(SIDEBAR_GROUPS_COOKIE, choices.closed);
  writeCookie(SIDEBAR_OPEN_GROUPS_COOKIE, choices.opened);
}

/** The closed and opened group ids, from the request on the server or the
 *  browser. */
export const readSidebarGroupChoices = createIsomorphicFn()
  .server(async (): Promise<SidebarGroupChoices> => {
    const { getCookie } = await importServerCookies();
    return {
      closed: parseSidebarGroups(getCookie(SIDEBAR_GROUPS_COOKIE)),
      opened: parseSidebarGroups(getCookie(SIDEBAR_OPEN_GROUPS_COOKIE)),
    };
  })
  .client(async (): Promise<SidebarGroupChoices> =>
    parseSidebarGroupChoices(document.cookie),
  );
